import {Suspense, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {borderRadius, calculateRemSize, flex, flexDirection, mb, ml, p, ph, pv, w100} from "@pg-design/helpers-css";
import {CalendarCheckIcon, FiltersIcon, HorizontalListIcon, PropertyPlanIcon, SortIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize as PS} from "@pg-design/pagination-module";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {useHidePricesAbTest} from "../../../hooks/use_hide_prices_ab_test";
import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {getCurrency} from "../../../utils/get_currency";
import {ILatestQuery} from "../../../utils/latest_query";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {IUseMapOfferListQueryResult} from "../../hooks/use_map_offer_list_query";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {IOfferDetail} from "../../types/IOfferDetail";
import {MapOfferExpandablePanelOfferSwitcher} from "./MapOfferExpandablePanelOfferSwitcher";

interface IProps {
    offer: IMapOfferSidebar;
    handleSetOfferId: (offerId: number | null) => void;
    latestQuery: ILatestQuery;
    mapOfferListQuery?: IUseMapOfferListQueryResult;
    handleQuickViewOpen: (id: number) => void;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
}

export const MapOfferExpandablePanelOffer = (props: IProps) => {
    const {offer, handleSetOfferId, latestQuery, mapOfferListQuery, handleQuickViewOpen, propertyListType, onPropertyListTypeClick} = props;

    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);
    const propertyListFilters = isEmpty(latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, latestSearchFilters);

    const propertyList = usePropertyList(offer.id, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);
    const {page, pageCount, requestState, properties, sort, onPageChange, pageIsLoading, onSortChange, onFiltersChange} = propertyList;

    const {isMobile} = useUserDevice();
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const {isHiddenVariant} = useHidePricesAbTest();
    const {data: userInfo} = useGetUserProfileQuery();

    const shouldShowPrice = !isHiddenVariant || Boolean(!isHiddenVariant && userInfo && userInfo.phone);

    const handlers = {
        closeFilters: () => {
            setIsFiltersModalOpen(false);
        },
        saveFilters: (values: IFiltersFormValues) => {
            const floorChoicesMap: {[index: number]: boolean} = {
                1: values.floorGround,
                2: values.floorGroundWithGarden,
                3: values.floorOneToFour,
                4: values.floorFivePlus,
                5: values.floorLast,
                6: values.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));
            onFiltersChange({
                ...(values.area?.lower && {area_0: values.area.lower}),
                ...(values.area?.upper && {area_1: values.area.upper}),
                ...(values.rooms?.lower && {rooms_0: values.rooms.lower}),
                ...(values.rooms?.upper && {rooms_1: values.rooms.upper}),
                floor_choices: floorChoices
            });
            setIsFiltersModalOpen(false);
        },
        saveSort: (value: number) => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === value)?.slug || null;
            onSortChange(sortSlugValue);
            setIsSortModalOpen(false);
        },
        closeSort: () => {
            setIsSortModalOpen(false);
        },
        openSort: () => {
            setIsSortModalOpen(true);
        },
        openFilters: () => {
            setIsFiltersModalOpen(true);
        },
        clearSort: () => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === SortOptionsValues.LOWEST_PRICE)?.slug || null;
            onSortChange(sortSlugValue, propertyListFilters);
            setIsSortModalOpen(false);
        },
        handlePageChange: async (page: number) => {
            onPageChange(page);
            headerRef.current?.scrollIntoView({behavior: "smooth"});
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <div css={wrapper}>
                <div css={headerStyle}>
                    <Image alt="logo dewelopera" width="72px" height="54px" src={offer.vendor.logo?.v_log_120x90} />

                    <MapOfferExpandablePanelOfferSwitcher
                        mapOfferListQuery={mapOfferListQuery}
                        offerId={offer.id}
                        handleSetOfferId={handleSetOfferId}
                        vendorId={offer.vendor.id}
                        propertyListType={propertyListType}
                    />
                </div>

                <div css={contentStyle}>
                    <div css={infoBoxWrapper}>
                        <Image alt={offer.name} src={offer.main_image.m_img_224x147} width="123px" height="116px" loading="lazy" />

                        <div css={infoBoxContainer}>
                            <Text as="div" variant="info_txt_2" css={constructionDatePill}>
                                <CalendarCheckIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />

                                <Text as="span" variant="info_txt_2" strong>
                                    {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarter)}
                                </Text>
                            </Text>

                            <Text>{offer.name}</Text>

                            {shouldShowPrice && !!offer?.stats?.ranges_price_m2_min && (
                                <Text as="div" variant="body_copy_1" mb={1.5} strong>
                                    <Highlight>
                                        {`od ${numberFormat(offer?.stats?.ranges_price_m2_min ?? 0)}`} {getCurrency({country: offer.region.country})}/m
                                        <sup>2</sup>
                                    </Highlight>
                                </Text>
                            )}

                            <Text variant="info_txt_2">{offer.address}</Text>

                            {!!offer.stats?.distance_from_region.distance && (
                                <Text variant="info_txt_2" strong>{`(${offer.stats.distance_from_region.distance} km od centrum)`}</Text>
                            )}
                        </div>
                    </div>

                    <div css={tilesHeaderWrapper} ref={headerRef}>
                        <div css={headerButtonContainer}>
                            <StyledButton
                                isActive={propertyListType === PropertyListType.TILES}
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TILES)}
                                iconLeft={PropertyPlanIcon}
                            >
                                Rzuty
                            </StyledButton>

                            <StyledButton
                                isActive={propertyListType === PropertyListType.TABLE}
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TABLE)}
                                iconLeft={HorizontalListIcon}
                            >
                                Lista
                            </StyledButton>
                        </div>

                        <div css={headerButtonContainer}>
                            <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" onClick={handlers.openFilters} />
                            <SortIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" css={ml(1)} onClick={handlers.openSort} />
                        </div>
                    </div>

                    {propertyListType === PropertyListType.TABLE && (
                        <div css={tableWrapper}>
                            <PropertyListTable
                                requestState={requestState}
                                offer={offer}
                                properties={properties}
                                sort={sort.currentSort}
                                onSortChange={(value) => onSortChange(value, propertyListFilters)}
                                onPageChange={handlers.handlePageChange}
                                pageCount={pageCount}
                                pageIsLoading={pageIsLoading}
                                currentPage={page.currentPage}
                                sourceSection={ApplicationSourceSection.SIDEBAR}
                                css={modalPropertyList}
                                hideCell={{
                                    propertyNumber: true
                                }}
                                withoutLink
                                handleCellClick={handleQuickViewOpen}
                                textLinkButton
                                loaderWrapperBackground="var(--colors-gray-100)"
                                gtmMeta={{
                                    listId: "zobacz_na_mapie",
                                    listName: "Zobacz na mapie"
                                }}
                            />
                        </div>
                    )}

                    {propertyListType === PropertyListType.TILES && (
                        <PropertyListTileList
                            requestState={requestState}
                            properties={properties}
                            offer={offer as unknown as IOfferDetail}
                            css={modalPropertyList}
                            gridLayoutCols={{md: 2, lg: 2}}
                            onPropertyTileClick={(property) => handleQuickViewOpen(properties.findIndex(({id}) => id === property.id))}
                            propertyBoxComponent="div"
                            gtmMeta={{
                                listId: "zobacz_na_mapie",
                                listName: "Zobacz na mapie"
                            }}
                        />
                    )}

                    {pageCount > 0 && (
                        <Pagination
                            css={mb(2)}
                            currentPage={page.currentPage}
                            pageCount={pageCount}
                            onPageChange={handlers.handlePageChange}
                            size={isMobile ? PS.SM : PS.MD}
                            siblingsCount={isMobile ? 0 : 1}
                        />
                    )}
                </div>
            </div>

            <PropertyListFiltersModal
                offerType={offer.type}
                isOpen={isFiltersModalOpen && !!isMobile}
                handleClose={handlers.closeFilters}
                handleConfirm={handlers.saveFilters}
            />

            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={isSortModalOpen}
                defaultValue={propertyListSortOptions.find((option) => option.slug === sort.currentSort)?.value || SortOptionsValues.LOWEST_PRICE}
            />
        </Suspense>
    );
};

const wrapper = css`
    ${w100};
`;

const headerStyle = css`
    ${flex("center", "space-between")};
    background-color: #fff;
    ${ph(1.5)};
`;

const modalPropertyList = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
`;

const tableWrapper = (theme: Theme) => css`
    width: 100vw;
    margin-left: -${calculateRemSize(1.5)};

    @media (min-width: ${theme.breakpoints.xs}) {
        width: 100%;
        margin-left: initial;
    }
`;

const tilesHeaderWrapper = css`
    ${flex("center", "space-between")};
    ${w100};
`;

interface IStyledButtonProps {
    isActive: boolean;
}

const StyledButton = styled(Button)<IStyledButtonProps>`
    ${p(2, 1)};
    ${({isActive, theme}) =>
        !isActive &&
        css`
            color: ${theme.colors.gray[600]};
            fill: ${theme.colors.gray[600]};
        `}

    :hover {
        color: ${({theme}) => theme.colors.secondary};
        fill: ${({theme}) => theme.colors.secondary};
    }
`;

const headerButtonContainer = css`
    ${flex("center")}
    gap: ${calculateRemSize(2)};
`;

const contentStyle = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
    ${ph(1.5)};
    max-height: calc(100dvh - 60px - 54px);
    overflow-y: auto;
    overflow-x: hidden;
`;

const infoBoxWrapper = css`
    ${flex("flex-start")};
    gap: ${calculateRemSize(1)};
    ${pv(2)};
`;

const infoBoxContainer = css`
    ${flex("flex-start")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(0.5)};
`;

const constructionDatePill = (theme: Theme) => css`
    ${flex("center")};
    ${p(0.5)};
    gap: ${calculateRemSize(0.5)};
    ${borderRadius(2)};
    background-color: ${theme.colors.primary};
`;
import {Suspense, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {Button} from "@pg-design/button-module";
import {borderRadius, calculateRemSize, flex, flexDirection, mb, ml, p, ph, pv, w100} from "@pg-design/helpers-css";
import {CalendarCheckIcon, FiltersIcon, HorizontalListIcon, PropertyPlanIcon, SortIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize as PS} from "@pg-design/pagination-module";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {useHidePricesAbTest} from "../../../hooks/use_hide_prices_ab_test";
import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {getCurrency} from "../../../utils/get_currency";
import {ILatestQuery} from "../../../utils/latest_query";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {IUseMapOfferListQueryResult} from "../../hooks/use_map_offer_list_query";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {IOfferDetail} from "../../types/IOfferDetail";
import {MapOfferExpandablePanelOfferSwitcher} from "./MapOfferExpandablePanelOfferSwitcher";

interface IProps {
    offer: IMapOfferSidebar;
    handleSetOfferId: (offerId: number | null) => void;
    latestQuery: ILatestQuery;
    mapOfferListQuery?: IUseMapOfferListQueryResult;
    handleQuickViewOpen: (id: number) => void;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
}

export const MapOfferExpandablePanelOffer = (props: IProps) => {
    const {offer, handleSetOfferId, latestQuery, mapOfferListQuery, handleQuickViewOpen, propertyListType, onPropertyListTypeClick} = props;

    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);
    const propertyListFilters = isEmpty(latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, latestSearchFilters);

    const propertyList = usePropertyList(offer.id, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);
    const {page, pageCount, requestState, properties, sort, onPageChange, pageIsLoading, onSortChange, onFiltersChange} = propertyList;

    const {isMobile} = useUserDevice();
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const {isHiddenVariant} = useHidePricesAbTest();
    const {data: userInfo} = useGetUserProfileQuery();

    const shouldShowPrice = !isHiddenVariant || Boolean(!isHiddenVariant && userInfo && userInfo.phone);

    const handlers = {
        closeFilters: () => {
            setIsFiltersModalOpen(false);
        },
        saveFilters: (values: IFiltersFormValues) => {
            const floorChoicesMap: {[index: number]: boolean} = {
                1: values.floorGround,
                2: values.floorGroundWithGarden,
                3: values.floorOneToFour,
                4: values.floorFivePlus,
                5: values.floorLast,
                6: values.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));
            onFiltersChange({
                ...(values.area?.lower && {area_0: values.area.lower}),
                ...(values.area?.upper && {area_1: values.area.upper}),
                ...(values.rooms?.lower && {rooms_0: values.rooms.lower}),
                ...(values.rooms?.upper && {rooms_1: values.rooms.upper}),
                floor_choices: floorChoices
            });
            setIsFiltersModalOpen(false);
        },
        saveSort: (value: number) => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === value)?.slug || null;
            onSortChange(sortSlugValue);
            setIsSortModalOpen(false);
        },
        closeSort: () => {
            setIsSortModalOpen(false);
        },
        openSort: () => {
            setIsSortModalOpen(true);
        },
        openFilters: () => {
            setIsFiltersModalOpen(true);
        },
        clearSort: () => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === SortOptionsValues.LOWEST_PRICE)?.slug || null;
            onSortChange(sortSlugValue, propertyListFilters);
            setIsSortModalOpen(false);
        },
        handlePageChange: async (page: number) => {
            onPageChange(page);
            headerRef.current?.scrollIntoView({behavior: "smooth"});
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <div css={wrapper}>
                <div css={headerStyle}>
                    <Image alt="logo dewelopera" width="72px" height="54px" src={offer.vendor.logo?.v_log_120x90} />

                    <MapOfferExpandablePanelOfferSwitcher
                        mapOfferListQuery={mapOfferListQuery}
                        offerId={offer.id}
                        handleSetOfferId={handleSetOfferId}
                        vendorId={offer.vendor.id}
                        propertyListType={propertyListType}
                    />
                </div>

                <div css={contentStyle}>
                    <div css={infoBoxWrapper}>
                        <Image alt={offer.name} src={offer.main_image.m_img_224x147} width="123px" height="116px" loading="lazy" />

                        <div css={infoBoxContainer}>
                            <Text as="div" variant="info_txt_2" css={constructionDatePill}>
                                <CalendarCheckIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />

                                <Text as="span" variant="info_txt_2" strong>
                                    {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarter)}
                                </Text>
                            </Text>

                            <Text>{offer.name}</Text>

                            {shouldShowPrice && !!offer?.stats?.ranges_price_m2_min && (
                                <Text as="div" variant="body_copy_1" mb={1.5} strong>
                                    <Highlight>
                                        {`od ${numberFormat(offer?.stats?.ranges_price_m2_min ?? 0)}`} {getCurrency({country: offer.region.country})}/m
                                        <sup>2</sup>
                                    </Highlight>
                                </Text>
                            )}

                            <Text variant="info_txt_2">{offer.address}</Text>

                            {!!offer.stats?.distance_from_region.distance && (
                                <Text variant="info_txt_2" strong>{`(${offer.stats.distance_from_region.distance} km od centrum)`}</Text>
                            )}
                        </div>
                    </div>

                    <div css={tilesHeaderWrapper} ref={headerRef}>
                        <div css={headerButtonContainer}>
                            <StyledButton
                                isActive={propertyListType === PropertyListType.TILES}
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TILES)}
                                iconLeft={PropertyPlanIcon}
                            >
                                Rzuty
                            </StyledButton>

                            <StyledButton
                                isActive={propertyListType === PropertyListType.TABLE}
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TABLE)}
                                iconLeft={HorizontalListIcon}
                            >
                                Lista
                            </StyledButton>
                        </div>

                        <div css={headerButtonContainer}>
                            <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" onClick={handlers.openFilters} />
                            <SortIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" css={ml(1)} onClick={handlers.openSort} />
                        </div>
                    </div>

                    {propertyListType === PropertyListType.TABLE && (
                        <div css={tableWrapper}>
                            <PropertyListTable
                                requestState={requestState}
                                offer={offer}
                                properties={properties}
                                sort={sort.currentSort}
                                onSortChange={(value) => onSortChange(value, propertyListFilters)}
                                onPageChange={handlers.handlePageChange}
                                pageCount={pageCount}
                                pageIsLoading={pageIsLoading}
                                currentPage={page.currentPage}
                                sourceSection={ApplicationSourceSection.SIDEBAR}
                                css={modalPropertyList}
                                hideCell={{
                                    propertyNumber: true
                                }}
                                withoutLink
                                handleCellClick={handleQuickViewOpen}
                                textLinkButton
                                loaderWrapperBackground="var(--colors-gray-100)"
                                gtmMeta={{
                                    listId: "zobacz_na_mapie",
                                    listName: "Zobacz na mapie"
                                }}
                            />
                        </div>
                    )}

                    {propertyListType === PropertyListType.TILES && (
                        <PropertyListTileList
                            requestState={requestState}
                            properties={properties}
                            offer={offer as unknown as IOfferDetail}
                            css={modalPropertyList}
                            gridLayoutCols={{md: 2, lg: 2}}
                            onPropertyTileClick={(property) => handleQuickViewOpen(properties.findIndex(({id}) => id === property.id))}
                            propertyBoxComponent="div"
                            gtmMeta={{
                                listId: "zobacz_na_mapie",
                                listName: "Zobacz na mapie"
                            }}
                        />
                    )}

                    {pageCount > 0 && (
                        <Pagination
                            css={mb(2)}
                            currentPage={page.currentPage}
                            pageCount={pageCount}
                            onPageChange={handlers.handlePageChange}
                            size={isMobile ? PS.SM : PS.MD}
                            siblingsCount={isMobile ? 0 : 1}
                        />
                    )}
                </div>
            </div>

            <PropertyListFiltersModal
                offerType={offer.type}
                isOpen={isFiltersModalOpen && !!isMobile}
                handleClose={handlers.closeFilters}
                handleConfirm={handlers.saveFilters}
            />

            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={isSortModalOpen}
                defaultValue={propertyListSortOptions.find((option) => option.slug === sort.currentSort)?.value || SortOptionsValues.LOWEST_PRICE}
            />
        </Suspense>
    );
};

const wrapper = css`
    ${w100};
`;

const headerStyle = css`
    ${flex("center", "space-between")};
    background-color: #fff;
    ${ph(1.5)};
`;

const modalPropertyList = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
`;

const tableWrapper = (theme: Theme) => css`
    width: 100vw;
    margin-left: -${calculateRemSize(1.5)};

    @media (min-width: ${theme.breakpoints.xs}) {
        width: 100%;
        margin-left: initial;
    }
`;

const tilesHeaderWrapper = css`
    ${flex("center", "space-between")};
    ${w100};
`;

interface IStyledButtonProps {
    isActive: boolean;
}

const StyledButton = styled(Button)<IStyledButtonProps>`
    ${p(2, 1)};
    ${({isActive, theme}) =>
        !isActive &&
        css`
            color: ${theme.colors.gray[600]};
            fill: ${theme.colors.gray[600]};
        `}

    :hover {
        color: ${({theme}) => theme.colors.secondary};
        fill: ${({theme}) => theme.colors.secondary};
    }
`;

const headerButtonContainer = css`
    ${flex("center")}
    gap: ${calculateRemSize(2)};
`;

const contentStyle = (theme: Theme) => css`
    background-color: ${theme.colors.gray[100]};
    ${ph(1.5)};
    max-height: calc(100dvh - 60px - 54px);
    overflow-y: auto;
    overflow-x: hidden;
`;

const infoBoxWrapper = css`
    ${flex("flex-start")};
    gap: ${calculateRemSize(1)};
    ${pv(2)};
`;

const infoBoxContainer = css`
    ${flex("flex-start")};
    ${flexDirection("column")};
    gap: ${calculateRemSize(0.5)};
`;

const constructionDatePill = (theme: Theme) => css`
    ${flex("center")};
    ${p(0.5)};
    gap: ${calculateRemSize(0.5)};
    ${borderRadius(2)};
    background-color: ${theme.colors.primary};
`;
